import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { tintColor } from '../lib/colorTint';

/**
 * Multi-select filter with Incluir/Excluir tabs.
 *
 * value: { mode: 'include'|'exclude', ids: number[] }
 *   ids may contain 0 to mean "Nenhum" (null on the backend).
 */
export default function MultiSelectFilter({ label, options, value, onChange, allowNenhum = true }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const mode = value?.mode || 'include';
  const ids = value?.ids || [];

  useEffect(() => {
    const click = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', click);
    return () => document.removeEventListener('mousedown', click);
  }, []);

  const setMode = (m) => onChange({ mode: m, ids });
  const toggle = (id) => {
    const next = ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
    onChange({ mode, ids: next });
  };

  const summary = ids.length === 0
    ? 'Todos'
    : `${mode === 'include' ? 'Inclui' : 'Exclui'}: ${ids.length} ${ids.length === 1 ? 'item' : 'itens'}`;

  return (
    <div ref={wrapRef} className="relative">
      {label && <label className="text-sm text-slate-600 dark:text-slate-400 mb-1 block">{label}</label>}
      <button type="button" onClick={() => setOpen((o) => !o)}
        className={`input-md flex items-center justify-between w-full text-left ${ids.length ? (mode === 'include' ? 'border-indigo-500' : 'border-rose-400') : ''}`}>
        <span className={ids.length ? 'text-slate-800 dark:text-slate-100' : 'text-slate-500 dark:text-slate-400'}>{summary}</span>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && (
        <div className="absolute z-50 mt-1 left-0 right-0 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl max-h-72 overflow-hidden flex flex-col">
          <div className="flex p-1 bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700 gap-1">
            <button type="button" onClick={() => setMode('include')}
              className={`flex-1 py-1.5 rounded-md text-sm font-medium ${mode === 'include' ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300' : 'text-slate-600 dark:text-slate-400'}`}>
              Incluir
            </button>
            <button type="button" onClick={() => setMode('exclude')}
              className={`flex-1 py-1.5 rounded-md text-sm font-medium ${mode === 'exclude' ? 'bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300' : 'text-slate-600 dark:text-slate-400'}`}>
              Excluir
            </button>
          </div>
          <div className="overflow-y-auto py-1">
            {allowNenhum && (
              <Row checked={ids.includes(0)} onClick={() => toggle(0)} color={null} label="Nenhum" italic />
            )}
            {options.map((o) => (
              <Row key={o.id} checked={ids.includes(o.id)} onClick={() => toggle(o.id)} color={o.color} label={o.name} />
            ))}
          </div>
          {ids.length > 0 && (
            <button type="button" onClick={() => onChange({ mode, ids: [] })}
              className="border-t border-slate-200 dark:border-slate-700 py-1.5 text-xs text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/30">
              Limpar seleção
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ checked, onClick, color, label, italic }) {
  const bg = color ? tintColor(color, 0.14) : undefined;
  return (
    <button type="button" onClick={onClick}
      style={bg ? { backgroundColor: bg } : undefined}
      className="w-full px-3 py-1.5 flex items-center gap-2 hover:brightness-95 dark:hover:brightness-110 text-left">
      <span className={`w-4 h-4 rounded border ${checked ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300 dark:border-slate-600'} flex items-center justify-center`}>
        {checked && <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none"><path d="M2.5 6.5L5 9L9.5 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
      </span>
      {color && <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: color }} />}
      <span className={`text-sm text-slate-700 dark:text-slate-200 truncate ${italic ? 'italic text-slate-500 dark:text-slate-400' : ''}`}>{label}</span>
    </button>
  );
}
